import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';

import { AddCartItem, Cart, CartBill, CartContext, CartItem, EMPTY_CART } from '../models/cart.model';
import { CartApiService } from './cart-api.service';
import { ConfirmService } from './confirm.service';
import { SessionService } from './session.service';

/** Thrown when the customer keeps their offer instead of applying the change. */
export class PromoChangeDeclined extends Error {
  constructor() {
    super('promo_change_declined');
    Object.setPrototypeOf(this, PromoChangeDeclined.prototype); // ES5 target: keep instanceof working
  }
}

/** The server's "this change kills the applied offer" response (HTTP 409). */
function isPromoNotEligible(e: any): boolean {
  return e?.status === 409 && e?.error?.error_code === 'promo_not_eligible';
}

/**
 * Server-backed cart store (authenticated only). Holds the last resolved cart
 * from the API and exposes derived count/subtotal/total; every mutation goes
 * through CartApiService and replaces the local snapshot with the server's
 * authoritative response (lines, availability flags, and computed bill).
 */
@Injectable({ providedIn: 'root' })
export class CartService {
  private readonly _cart = new BehaviorSubject<Cart>(EMPTY_CART);
  readonly cart$ = this._cart.asObservable();

  /** Count of AVAILABLE line quantities (unavailable lines are excluded from totals). */
  readonly count$ = this.cart$.pipe(map((c) => availableCount(c)));
  readonly subtotal$ = this.cart$.pipe(map((c) => c.bill.subtotal));
  readonly total$ = this.cart$.pipe(map((c) => c.bill.total));

  constructor(private api: CartApiService, private session: SessionService, private confirm: ConfirmService) {
    // Cart is per-account: load on sign-in, drop on sign-out.
    this.session.loggedIn$.subscribe((loggedIn) => (loggedIn ? this.load() : this._cart.next(EMPTY_CART)));
  }

  // ── snapshots ──
  get cart(): Cart { return this._cart.value; }
  get items(): CartItem[] { return this.cart.items; }
  get bill(): CartBill { return this.cart.bill; }
  get count(): number { return availableCount(this.cart); }
  get subtotal(): number { return this.cart.bill.subtotal; }
  get total(): number { return this.cart.bill.total; }

  /** Quantity in the cart for a given menu item id (across its lines). */
  qtyOf(menuItemId: string): number {
    return this.cart.items.filter((i) => i.menuItemId === menuItemId).reduce((s, i) => s + i.quantity, 0);
  }

  // ── operations (all replace the snapshot with the server response) ──
  load(branchId?: string | null): void {
    if (!this.session.loggedIn) { this._cart.next(EMPTY_CART); return; }
    this.api.get(branchId).subscribe({ next: (c) => this._cart.next(c), error: () => {} });
  }

  /**
   * Gate a line change behind the offer warning. Editing the cart's items while
   * an offer is applied can't keep both, so the customer decides up front:
   * "Continue" drops the offer (DELETE /cart/promo) and then applies the change,
   * "Keep offer" aborts with PromoChangeDeclined and nothing is sent. With no
   * offer applied the change goes straight through.
   */
  private confirmingOfferLoss(run: () => Observable<Cart>): Observable<Cart> {
    if (!this.cart.promoCode) return run();
    return this.confirm.ask({
      title: 'Your offer will be removed',
      message: 'Changing the items in your cart means your offer code no longer applies. Continuing will remove it.',
      confirmText: 'Continue',
      cancelText: 'Keep offer',
      danger: true,
    }).pipe(
      switchMap((ok) => (ok
        ? this.removePromo().pipe(switchMap(() => run()))
        : throwError(() => new PromoChangeDeclined()))),
    );
  }

  /** Add a configured line; caller shows the confirmation/handles errors. */
  addItem(req: AddCartItem): Observable<Cart> {
    return this.confirmingOfferLoss(() => this.api.addItem(req).pipe(tap((c) => this._cart.next(c))));
  }

  /**
   * Swap a line for a reconfigured one (edit from the cart). The API can only
   * PATCH quantity, so the new configuration is posted first and the old line
   * removed after — a failed add leaves the original untouched. If the server
   * merged the post into an existing identical line instead of creating one, the
   * merge target is resolved: merged into the edited line itself → reset it to
   * the requested quantity; merged into another line → just drop the old one.
   */
  replaceLine(lineId: string, req: AddCartItem): Observable<Cart> {
    const before = new Map(this.cart.items.map((i) => [i.id, i.quantity]));
    return this.confirmingOfferLoss(() => this.api.addItem(req).pipe(
      switchMap((c) => {
        const created = c.items.some((i) => !before.has(i.id));
        const old = c.items.find((i) => i.id === lineId);
        if (!created && old && old.quantity !== before.get(lineId)) return this.api.setQuantity(lineId, req.quantity);
        return this.api.removeLine(lineId);
      }),
      tap((c) => this._cart.next(c)),
      // Partial failure (added but not removed) — resync so the UI shows the truth.
      catchError((e) => { this.load(this.cart.branchId); return throwError(() => e); }),
    ));
  }

  /** Set a line's quantity (0 removes it). */
  setQuantity(lineId: string, qty: number): void {
    this.confirmingOfferLoss(() => this.api.setQuantity(lineId, qty).pipe(tap((c) => this._cart.next(c))))
      .subscribe({ next: () => {}, error: () => {} });
  }

  /**
   * Drop a line the server just created but can't sell (sold out). It was never charged, so the
   * applied offer isn't affected — no "your offer will be removed" prompt.
   */
  discardLine(lineId: string): void {
    this.api.removeLine(lineId).pipe(tap((c) => this._cart.next(c))).subscribe({ next: () => {}, error: () => {} });
  }

  removeLine(lineId: string): void {
    this.confirmingOfferLoss(() => this.api.removeLine(lineId).pipe(tap((c) => this._cart.next(c))))
      .subscribe({ next: () => {}, error: () => {} });
  }

  /** Apply a promo/offer — typed `{ code }` or scanned `{ qr }`; caller handles success/error. */
  applyPromo(body: { code?: string; qr?: string }): Observable<Cart> {
    return this.api.applyPromo(body).pipe(tap((c) => this._cart.next(c)));
  }
  /** Remove the applied offer. */
  removePromo(): Observable<Cart> {
    return this.api.removePromo().pipe(tap((c) => this._cart.next(c)));
  }

  clear(): void {
    if (!this.session.loggedIn) { this._cart.next(EMPTY_CART); return; }
    this.confirmingOfferLoss(() => this.api.clear().pipe(tap((c) => this._cart.next(c))))
      .subscribe({ next: () => {}, error: () => {} });
  }

  /** Sync order-type / branch / address / notes onto the server cart. */
  setContext(ctx: CartContext, dropPromo = false): Observable<Cart> {
    return this.api.setContext(ctx, dropPromo).pipe(tap((c) => this._cart.next(c)));
  }

  /**
   * Same as setContext, but handles the server's 409 `promo_not_eligible`:
   * switching order type / branch can make the applied offer invalid, so we show
   * the server's message, and on "continue" resend the identical change with
   * `drop_promo: true`. Keeping the offer errors with PromoChangeDeclined so the
   * caller can roll back whatever it changed optimistically.
   */
  setContextConfirmingPromoDrop(ctx: CartContext): Observable<Cart> {
    return this.setContext(ctx).pipe(
      catchError((e) => {
        if (!isPromoNotEligible(e)) return throwError(() => e);
        return this.confirm.ask({
          title: 'Your offer won’t apply anymore',
          message: e?.error?.message || 'This change means your offer code isn’t valid anymore. Continuing will remove it.',
          confirmText: 'Continue',
          cancelText: 'Keep offer',
          danger: true,
        }).pipe(
          switchMap((ok) => (ok ? this.setContext(ctx, true) : throwError(() => new PromoChangeDeclined()))),
        );
      }),
    );
  }

  /** Replace the cart snapshot (e.g. after a reorder replays into a fresh cart). */
  setCart(cart: Cart): void { this._cart.next(cart); }
}

function availableCount(c: Cart): number {
  return c.items.filter((i) => i.available).reduce((s, i) => s + i.quantity, 0);
}
